import { useState, type DragEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { SKILL_LEVELS } from "@/lib/skill-levels";
import { skillLevelBandBackground, skillLevelSwatch } from "@/lib/skill-level-style";
import { useThemePrefs } from "@/stores/theme-prefs";
import { SkillLevelInfoIcon } from "@/components/design/SkillLevelInfoIcon";
import { LockClosedIcon, LockOpenIcon } from "../lock-icons";
import { RequirementControls } from "./row-primitives";

// Bewerbungs-eigenes Fachwissen-Diagramm in der Optik des Profil-SkillsDiagram (save/):
// eine senkrechte Achse der 7 Stufen mit Level-Baendern, Skills als Chips auf ihrer Stufe,
// "Vorhanden" unten fuer Skills ohne Mindestlevel. Bewusst NICHT das Profil-Diagramm selbst — das haengt an EntryFields/
// SaveBar (ADR-0005) und Profil-Drafts. Muss/Kann steckt als Schloss am Chip (geschlossen =
// Muss, offen = Kann); Ziehen aendert nur die Stufe.

export type MatchSkillRequirement = "MUST" | "OPTIONAL";

export interface MatchSkillEntry {
  id: string;
  description: string;
  level: number; // 1..7, 0 = Vorhanden ohne Mindestlevel
  requirement: MatchSkillRequirement;
}

const UNRATED = 0;

function SkillTag({
  entry,
  active,
  dragging,
  readOnly,
  onDragStart,
  onDragEnd,
  onClick,
  onToggleLock,
}: {
  entry: MatchSkillEntry;
  active: boolean;
  dragging: boolean;
  readOnly?: boolean;
  onDragStart: (e: DragEvent) => void;
  onDragEnd: () => void;
  onClick: () => void;
  onToggleLock: () => void;
}) {
  const intl = useIntl();
  const isMust = entry.requirement === "MUST";
  const lockLabel = intl.formatMessage(
    isMust
      ? {
          id: "match.recruiting.must.tooltip",
          defaultMessage: "Muss-Kriterium: wer es nicht erfüllt, ist unqualifiziert. Klicken für Kann.",
        }
      : {
          id: "match.recruiting.kann.tooltip",
          defaultMessage: "Kann-Kriterium: zählt in den Passungs-Score. Klicken für Muss.",
        },
  );
  return (
    <span
      role="button"
      tabIndex={0}
      data-testid="match-skill-chip"
      data-requirement={entry.requirement}
      draggable={!readOnly}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className={`inline-flex max-w-full items-center gap-1.5 truncate rounded-full px-2.5 py-1 text-[12px] transition ${
        // Muss = gefuellt in Signalfarbe ohne Default-Rahmen; Kann = nur Signalfarben-Rahmenlinie.
        // Editing-Zustand hebt sich ueber die dickere Ringstaerke ab.
        active ? "ring-2 ring-accent/60" : isMust ? "ring-0" : "ring-1 ring-accent/40"
      } ${isMust ? "bg-accent text-on-accent" : "text-ink/80"} ${
        readOnly ? "" : "cursor-grab focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30 active:cursor-grabbing"
      } ${dragging ? "opacity-40" : ""}`}
    >
      {/* Schloss vorne, konsistent zu den Kriterien-Rows und der Ergebnis-Darstellung. */}
      <button
        type="button"
        disabled={readOnly}
        onClick={(e) => {
          e.stopPropagation();
          onToggleLock();
        }}
        aria-pressed={isMust}
        aria-label={lockLabel}
        title={lockLabel}
        data-testid="match-skill-lock"
        // Muss haelt das Kreis-Highlight dauerhaft; auf der gefuellten Flaeche in on-accent.
        className={`shrink-0 rounded-full p-0.5 transition [&>svg]:h-3.5 [&>svg]:w-3.5 ${
          isMust ? "bg-on-accent/15 text-on-accent" : "text-ink/40"
        } ${
          readOnly
            ? ""
            : `focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30 ${
                isMust ? "hover:text-on-accent/70" : "hover:text-accent"
              }`
        }`}
      >
        {isMust ? <LockClosedIcon /> : <LockOpenIcon />}
      </button>
      <span className="min-w-0 truncate">{entry.description || "–"}</span>
    </span>
  );
}

export function MatchSkillsDiagram({
  entries,
  readOnly = false,
  onPlace,
  onToggleRequirement,
  onAdd,
  onRename,
  onDelete,
}: {
  entries: MatchSkillEntry[];
  readOnly?: boolean;
  onPlace: (id: string, level: number) => void;
  onToggleRequirement: (id: string, requirement: MatchSkillRequirement) => void;
  onAdd: (description: string, level: number, requirement: MatchSkillRequirement) => void;
  onRename: (id: string, description: string) => void;
  onDelete: (id: string) => void;
}) {
  const intl = useIntl();
  const accentColor = useThemePrefs((s) => s.accentColor);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropLevel, setDropLevel] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState<
    { level: number; name: string; requirement: MatchSkillRequirement } | null
  >(null);

  const byLevel = new Map<number, MatchSkillEntry[]>();
  for (const e of entries) {
    const bucket = byLevel.get(e.level);
    if (bucket) bucket.push(e);
    else byLevel.set(e.level, [e]);
  }
  // Innerhalb eines Bandes: Muss-Chips zuerst (stabil), analog zur Row-Sortierung.
  for (const bucket of byLevel.values()) {
    bucket.sort((a, b) => Number(b.requirement === "MUST") - Number(a.requirement === "MUST"));
  }
  const unrated = byLevel.get(UNRATED) ?? [];
  const editingEntry = editingId ? entries.find((e) => e.id === editingId) ?? null : null;

  const dropHandlers = (level: number) =>
    readOnly
      ? {}
      : {
          onDragOver: (e: DragEvent) => {
            if (!draggingId) return;
            e.preventDefault();
            if (dropLevel !== level) setDropLevel(level);
          },
          onDragLeave: () => setDropLevel((d) => (d === level ? null : d)),
          onDrop: (e: DragEvent) => {
            e.preventDefault();
            const id = e.dataTransfer.getData("text/plain") || draggingId;
            if (id) onPlace(id, level);
            setDropLevel(null);
            setDraggingId(null);
          },
        };

  const tagProps = (e: MatchSkillEntry) => ({
    active: editingId === e.id,
    dragging: draggingId === e.id,
    readOnly,
    onClick: () => {
      setAdding(null);
      setEditingId((id) => (id === e.id ? null : e.id));
    },
    onToggleLock: () =>
      onToggleRequirement(e.id, e.requirement === "MUST" ? "OPTIONAL" : "MUST"),
    onDragStart: (ev: DragEvent) => {
      ev.dataTransfer.effectAllowed = "move";
      ev.dataTransfer.setData("text/plain", e.id);
      setDraggingId(e.id);
    },
    onDragEnd: () => {
      setDraggingId(null);
      setDropLevel(null);
    },
  });

  function renderAddButton(level: number) {
    if (readOnly) return null;
    return (
      <button
        type="button"
        aria-label={intl.formatMessage({
          id: "match.recruiting.addSkillHere",
          defaultMessage: "Fachwissen hier hinzufügen",
        })}
        data-testid="match-skill-add"
        onClick={() => {
          setEditingId(null);
          setAdding({ level, name: "", requirement: "OPTIONAL" });
        }}
        className="shrink-0 rounded-full p-1 text-ink/40 opacity-0 transition hover:text-accent focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ink/30 group-hover/level:opacity-100"
      >
        <svg
          viewBox="0 0 24 24"
          className="h-5 w-5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </button>
    );
  }

  return (
    <div>
      <div className="space-y-1">
        {[...SKILL_LEVELS].reverse().map((l) => {
          const swatch = skillLevelSwatch(l.value, accentColor);
          const levelName = intl.formatMessage({ id: l.nameId, defaultMessage: l.name });
          const skills = byLevel.get(l.value) ?? [];
          return (
            <div
              key={l.value}
              data-testid={`match-skill-level-${l.value}`}
              {...dropHandlers(l.value)}
              className={`group/level flex items-center gap-3 rounded-md px-2 py-2 transition ${
                dropLevel === l.value ? "ring-2 ring-accent/60" : ""
              }`}
              style={{ background: skillLevelBandBackground(l.value) }}
            >
              <div className="flex w-40 shrink-0 items-center gap-2">
                <span
                  className="flex h-7 w-7 items-center justify-center rounded-md text-[11px] font-medium tabular-nums ring-1 ring-ink/10"
                  style={{ background: swatch.background, color: swatch.textColor }}
                >
                  {l.value}
                </span>
                <span className="min-w-0 truncate text-[12px] text-ink/70">{levelName}</span>
                <SkillLevelInfoIcon
                  level={l}
                  className="shrink-0 opacity-0 transition group-hover/level:opacity-100 group-focus-within/level:opacity-100"
                />
              </div>
              <div className="flex min-h-7 flex-1 flex-wrap items-center gap-1.5">
                {skills.map((e) => (
                  <SkillTag key={e.id} entry={e} {...tagProps(e)} />
                ))}
              </div>
              {renderAddButton(l.value)}
            </div>
          );
        })}

        <div
          data-testid="match-skill-level-unrated"
          {...dropHandlers(UNRATED)}
          className={`group/level mt-1 flex items-center gap-3 rounded-md px-2 py-2 transition ${
            dropLevel === UNRATED ? "ring-2 ring-accent/60" : ""
          }`}
        >
          <div className="w-40 shrink-0 text-[12px] text-ink/45">
            {intl.formatMessage({ id: "match.recruiting.unrated", defaultMessage: "Vorhanden" })}
          </div>
          <div className="flex min-h-7 flex-1 flex-wrap items-center gap-1.5">
            {unrated.map((e) => (
              <SkillTag key={e.id} entry={e} {...tagProps(e)} />
            ))}
          </div>
          {renderAddButton(UNRATED)}
        </div>
      </div>

      {adding ? (
        <div className="mt-3 flex items-center gap-2 rounded-md bg-page p-2 ring-1 ring-ink/10">
          <input
            type="text"
            autoFocus
            data-testid="match-skill-add-input"
            value={adding.name}
            onChange={(e) => setAdding((s) => (s ? { ...s, name: e.target.value } : s))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && adding.name.trim()) {
                onAdd(adding.name.trim(), adding.level, adding.requirement);
                setAdding(null);
              } else if (e.key === "Escape") {
                setAdding(null);
              }
            }}
            placeholder={intl.formatMessage({ id: "match.recruiting.skillPlaceholder", defaultMessage: "z. B. Wundmanagement" })}
            className="h-8 min-w-0 flex-1 rounded-md bg-surface px-2 text-[12px] text-ink ring-1 ring-ink/10 focus:outline-none focus:ring-ink/30"
          />
          {/* Convenience: Muss direkt beim Anlegen setzen (gleicher Toggle wie in den Rows). */}
          <RequirementControls
            requirement={adding.requirement}
            onChange={(req) => setAdding((s) => (s ? { ...s, requirement: req } : s))}
          />
          <button
            type="button"
            disabled={!adding.name.trim()}
            data-testid="match-skill-add-confirm"
            onClick={() => {
              onAdd(adding.name.trim(), adding.level, adding.requirement);
              setAdding(null);
            }}
            className="shrink-0 rounded-md px-2 py-1 text-[12px] text-accent disabled:opacity-40"
          >
            <FormattedMessage id="common.add" defaultMessage="Hinzufügen" />
          </button>
          <button
            type="button"
            onClick={() => setAdding(null)}
            className="shrink-0 rounded-md px-2 py-1 text-[12px] text-ink/45 hover:text-ink"
          >
            <FormattedMessage id="common.cancel" defaultMessage="Abbrechen" />
          </button>
        </div>
      ) : null}

      {editingEntry ? (
        <div className="mt-3 flex items-center gap-2 rounded-md bg-page p-2 ring-1 ring-ink/10">
          <input
            type="text"
            data-testid="match-skill-edit-input"
            value={editingEntry.description}
            onChange={(e) => onRename(editingEntry.id, e.target.value)}
            className="h-8 min-w-0 flex-1 rounded-md bg-surface px-2 text-[12px] text-ink ring-1 ring-ink/10 focus:outline-none focus:ring-ink/30"
          />
          <button
            type="button"
            data-testid="match-skill-delete"
            onClick={() => {
              onDelete(editingEntry.id);
              setEditingId(null);
            }}
            className="shrink-0 rounded-md px-2 py-1 text-[12px] text-ink/45 hover:text-ink"
          >
            <FormattedMessage id="common.remove" defaultMessage="Entfernen" />
          </button>
          <button
            type="button"
            onClick={() => setEditingId(null)}
            className="shrink-0 rounded-md px-2 py-1 text-[12px] text-accent"
          >
            <FormattedMessage id="profile.rows.done" defaultMessage="Fertig" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
